import React from 'react';
import { Stack, Box, Typography, useTheme } from '@mui/material';
// Used by both empty states below. This import went missing during an earlier cleanup
// and TypeScript did NOT catch it — apps/app's typecheck aborts on a parse error in
// react-hook-form's .d.ts (TS 4.9 vs a TS 5.0 `const` type parameter), so src/ is never
// checked. See VRG-158-adjacent task for the toolchain fix.
import { ScanQrCode } from 'lucide-react';
import { Avatar } from '@repo/shadcn-ui';
import { formatedDate, formatActivityTime } from 'utils/date';
import { ScansListSkeleton } from 'components/pages/certificates/productPage/ScansListSkeleton';
import {
  getScanStatus,
  getScanLocationLabel,
  type ScanStatusInput,
} from '@repo/ui';
import { filterScans } from 'utils/scanFilters';
import { ScanStatusChip } from 'components/pages/certificates/ScanStatusChip';
import { ListRow } from 'components/UI';

export interface ScanItem {
  id: string;
  user?: {
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    avatar?: string;
  };
  created_at: string;
  metadata?: {
    location?: string;
    city?: string;
    country?: string;
  };
  type?: string;
  status?: string;
}

export interface ScansListProps {
  scans: ScanItem[];
  isLoading?: boolean;
  query?: string;
  selectedStatuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  selectedRange?: 'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null;
  selectedLocationIds?: string[];
  sortField?: 'owner_name' | 'status' | 'date' | 'location' | null;
  sortDirection?: 'ASC' | 'DESC' | null;
  additionalSorts?: Array<{ field: 'owner_name' | 'status' | 'date' | 'location'; direction: 'ASC' | 'DESC' }>;
}

export const ScansList: React.FC<ScansListProps> = ({
  scans,
  isLoading = false,
  query = '',
  selectedStatuses = [],
  dateFrom,
  dateTo,
  selectedRange,
  selectedLocationIds = [],
  sortField = null,
  sortDirection = null,
  additionalSorts = [],
}) => {
  const theme = useTheme();
  if (isLoading) {
    return <ScansListSkeleton />;
  }

  // VERA-476: filtering lives in the shared `filterScans` util so the MAP applies the
  // exact same predicate. It used to be inline here, which is why every control
  // silently affected the list only.
  const filteredScans = filterScans(scans, {
    query,
    selectedStatuses,
    selectedLocationIds,
    dateFrom,
    dateTo,
    selectedRange,
  });

  const getFieldValue = (scan: ScanItem, field: 'owner_name' | 'status' | 'date' | 'location') => {
    switch (field) {
      case 'owner_name': {
        const first = (scan.user?.first_name || '').trim();
        const last = (scan.user?.last_name || '').trim();
        const full = `${first} ${last}`.trim();
        return (full || scan.user?.name || scan.user?.email || '').toLowerCase();
      }
      case 'status':
        return (scan.status || '').toLowerCase();
      case 'date':
        return new Date(scan.created_at).getTime();
      case 'location':
        return (getScanLocationLabel(scan.metadata) || '').toLowerCase();
    }
  };

  const sortDescriptors: Array<{ field: 'owner_name' | 'status' | 'date' | 'location'; direction: 'ASC' | 'DESC' }> =
    [];
  if (sortField && sortDirection) sortDescriptors.push({ field: sortField, direction: sortDirection });
  if (additionalSorts?.length) sortDescriptors.push(...additionalSorts);

  const sortedScans =
    sortDescriptors.length === 0
      ? filteredScans
      : [...filteredScans].sort((a, b) => {
          for (const { field, direction } of sortDescriptors) {
            const av = getFieldValue(a, field);
            const bv = getFieldValue(b, field);

            let cmp = 0;

            if (typeof av === 'number' && typeof bv === 'number') {
              cmp = av - bv;
            } else {
              const as = String(av || '');
              const bs = String(bv || '');
              cmp = as.localeCompare(bs, undefined, { sensitivity: 'base' });
            }

            if (cmp !== 0) return direction === 'ASC' ? cmp : -cmp;
          }
          return 0;
        });

  if (!scans || scans.length === 0) {
    return (
      <Stack
        sx={{
          textAlign: 'center',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '276px',
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: theme => theme.palette.grey[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: theme => `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <ScanQrCode size={24} color={theme.palette.text.primary} />
        </Box>
        <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>No scans yet</Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 320, fontSize: '14px' }}>
          Scans will appear here once someone scans this certificate.
        </Typography>
      </Stack>
    );
  }

  if (filteredScans.length === 0) {
    return (
      <Stack
        sx={{
          textAlign: 'center',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '276px',
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: theme => theme.palette.grey[50],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: theme => `1px solid ${theme.palette.grey[300]}`,
          }}
        >
          <ScanQrCode size={24} color={theme.palette.text.primary} />
        </Box>
        <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>
          No scans found
        </Typography>
        <Typography sx={{ color: 'text.secondary', maxWidth: 320, fontSize: '14px' }}>
          Try adjusting your search to find what you're looking for.
        </Typography>
      </Stack>
    );
  }

  return (
    // spacing={0}: rows sit flush and the hairline IS the separator. The old 16px
    // Stack gap left that hairline floating well above the next row.
    <Stack spacing={0}>
      {sortedScans.map(scan => {
        // Best-available label: reverse-geocoded address → City, State, Country →
        // coarse geoip country → coordinates. See getScanLocationLabel for why
        // `metadata.location` can't be read directly.
        //
        // Deliberately NOT defaulted to "Location not available" — the row omits the
        // line entirely when there's nothing to say. Render-token events never carry
        // coordinates, so that placeholder was a whole wasted line on every one of them.
        const location = getScanLocationLabel(scan.metadata);

        // Null when we don't know who scanned — NOT "Anonymous". Absence of a name is
        // already the signal; printing the word on 39 of 52 rows made the least
        // informative string the loudest thing in the list.
        const userName =
          scan.user?.name ||
          (scan.user?.first_name && scan.user?.last_name
            ? `${scan.user.first_name} ${scan.user.last_name}`
            : scan.user?.first_name || scan.user?.last_name || null);
        // VERA-476: real scan outcome, resolved by the shared @repo/ui resolver rather
        // than the previously hardcoded 'active'. Verified scans render NO chip — that
        // is the expected outcome, so a chip on every row would carry no information
        // and bury the exceptions (invalid / tampered / suspicious link).
        const scanStatus = getScanStatus(scan as ScanStatusInput);

        return (
          // Two columns: WHO/WHERE on the left, WHEN/WHAT on the right (VERA-476).
          // Scanning the list vertically, the left edge stays a clean identity column
          // while time and any exception chip group together on the right. Most rows are
          // verified and therefore chip-less, so the right column is usually just a
          // timestamp and stays quiet.
          //
          // VERA-978: that layout now lives in the shared `ListRow` — the Activity
          // timeline needs the identical shape, so this row supplies content only.
          // `truncate` because a name/email/address is unbounded and two-line parity
          // matters more here than reading the whole string.
          <ListRow
            key={scan.id}
            // Avatar on EVERY row, anonymous included. An earlier pass showed it only
            // for known users, which made the one identified scan pop — but at the cost
            // of two different row shapes, and inconsistent rows are harder to scan than
            // a repeated placeholder. Parity wins.
            //
            // photo → INITIALS → profile outline. `initials` is already a prop on the
            // shared Avatar, so this is a pass-through, not a hand-rolled fallback.
            // Anonymous scans pass undefined and fall through to the outline: "AN" would
            // invent an identity we don't have.
            //
            // size='md' (40px) to fill the shared leading slot — it was 'sm' (32px),
            // which left the row's left column looking under-filled next to Activity's.
            leading={
              <Avatar
                src={scan.user?.avatar ?? undefined}
                initials={
                  userName
                    ? userName
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map(word => word[0])
                        .join('')
                    : undefined
                }
                size='md'
                noImageIcon='profile'
              />
            }
            // The user cell's title is ALWAYS the user.
            label={userName || 'Anonymous'}
            // Location sits under the name and is ALWAYS rendered — an em-dash when
            // unknown — so every row is exactly two lines and the list holds vertical
            // parity whether or not a scan has a location or a chip.
            sublabel={location || '—'}
            sublabelMuted={!location}
            truncate
            // Chip and time share ONE line so the right column never grows the row.
            // Stacking them made chip rows 89px against 73px without — the exact
            // ragged rhythm this layout is meant to avoid.
            meta={<ScanStatusChip status={scanStatus} />}
            time={formatActivityTime(scan.created_at)}
            timeTitle={formatedDate({ date: scan.created_at, format: 'MMM do yyyy, h:mm a' })}
          />
        );
      })}
    </Stack>
  );
};

export default ScansList;
